import { useEffect, useRef } from "react";
import { NavLink } from "react-router";
import { X } from "lucide-react";
import { NAV, SITE } from "../../lib/site";
import { ButtonLink } from "../ui/Button";
import { cn } from "../../lib/utils";

/**
 * Full-screen mobile navigation dialog (APG dialog pattern):
 * focus is trapped, Escape closes, background scroll is locked, and focus
 * returns to the trigger on close (handled by the caller).
 */
export function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    document.body.style.overflow = "hidden";

    const focusables = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusables[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
    >
      <button
        type="button"
        className="absolute inset-0 h-full w-full bg-ink/40 backdrop-blur-sm"
        aria-label="Close navigation"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="absolute inset-x-0 top-0 origin-top bg-surface p-5 shadow-xl"
      >
        <div className="mb-6 flex items-center justify-between">
          <span className="eyebrow">Menu</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="inline-flex size-11 items-center justify-center rounded-lg text-ink-soft hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Primary">
          <ul className="flex flex-col gap-1">
            {NAV.map((item) => (
              <li key={item.href}>
                <NavLink
                  to={item.href}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "flex min-h-11 items-center rounded-lg px-3 text-lg font-medium text-ink transition hover:bg-surface-2",
                      isActive && "text-brand-600 dark:text-brand-500",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <ButtonLink to="/contact" size="lg" className="mt-6 w-full" onClick={onClose}>
          {SITE.primaryCta}
        </ButtonLink>
      </div>
    </div>
  );
}
